import imageManifest from '../data/portfolio-images.json';

// imageManifest is generated by scripts/optimize-portfolio-images.mjs.
// Keys are the full-size file names the page arrays (and the lightbox) use.
const toSrcSet = (candidates) =>
    candidates.map(({ file, width }) => `/${file} ${width}w`).join(', ');

const largestOf = (candidates) =>
    candidates.reduce((best, candidate) => (candidate.width > best.width ? candidate : best), candidates[0]);

const PICTURE_SOURCES = Object.fromEntries(
    Object.entries(imageManifest).map(([file, entry]) => [
        file,
        {
            width: entry.width,
            height: entry.height,
            avif: toSrcSet(entry.avif),
            webp: toSrcSet(entry.webp),
            largestAvif: entry.avif.length > 0 ? `/${largestOf(entry.avif).file}` : null
        }
    ])
);

/**
 * A gallery photograph.
 * - variant "responsive" (default): AVIF + WebP width candidates chosen by `sizes`.
 * - variant "large": the lightbox image — the largest AVIF, the full WebP as fallback.
 * Files missing from the manifest fall back to a plain <img> of the file itself.
 */
export default function PortfolioPicture({
    file,
    alt,
    sizes,
    variant = 'responsive',
    loading = 'lazy',
    fetchPriority
}) {
    const sources = PICTURE_SOURCES[file];
    const src = `/${file}`;

    if (!sources) {
        return (
            <img
                src={src}
                alt={alt}
                loading={loading}
                decoding="async"
                fetchPriority={fetchPriority}
            />
        );
    }

    const isLarge = variant === 'large';

    return (
        <picture>
            {isLarge
                ? sources.largestAvif && <source type="image/avif" srcSet={sources.largestAvif} />
                : <source type="image/avif" srcSet={sources.avif} sizes={sizes} />}
            <img
                src={src}
                srcSet={isLarge ? undefined : sources.webp}
                sizes={isLarge ? undefined : sizes}
                width={sources.width}
                height={sources.height}
                alt={alt}
                loading={loading}
                decoding="async"
                fetchPriority={fetchPriority}
            />
        </picture>
    );
}
